import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Package2, Printer, Search, CheckCircle, Clock, XCircle, Truck, Calendar, User, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice, getDomain } from '@/lib/utils';
import { useStoreInfo } from '@/lib/store';
import { getFullImageUrl } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface OrderDetail {
  payment_id: string;
  product_name: string;
  product_variant: string;
  variant_qty: number;
  status?: string;
  product_amount: number;
}

interface PaymentInfo {
  store_id: number;
  order_id: string;
  date: string;
  goods_amount: number;
  shipping_fee: number;
  shipmentType: string;
  note: string;
  orderStatus?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  shipping_address?: string;
  payment_method?: string;
}

interface OrderResponse {
  status: boolean;
  message: string;
  orderDetails?: OrderDetail[];
  payment_info?: PaymentInfo;
  order_status?: string;
}

export function TrackPage() {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderResponse | null>(null);
  const { storeInfo } = useStoreInfo();
  const pageTitle = `Track Order${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  // Use hero slider image or fallback to gradient
  const heroImage = storeInfo?.heroSlider?.[0]
    ? getFullImageUrl(storeInfo.heroSlider[0])
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const domain = getDomain();
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/orders/track/${encodeURIComponent(trackingId)}?domain=${domain}`);
      const data: OrderResponse = await response.json();
      setOrderStatus(data);
      if (!data.status) {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch order status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('order-details-section');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Details - ${orderStatus?.orderDetails?.[0]?.payment_id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section h3 { color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .item { padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
            .total { font-weight: bold; font-size: 1.2em; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'text-blue-500 dark:text-blue-400';
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'completed':
      case 'order concluded':
      case 'delivered':
        return 'text-green-500 dark:text-green-400';
      case 'pending':
      case 'processing':
        return 'text-yellow-500 dark:text-yellow-400';
      case 'cancelled':
      case 'canceled':
        return 'text-red-500 dark:text-red-400';
      case 'shipped':
      case 'in transit':
        return 'text-blue-500 dark:text-blue-400';
      default:
        return 'text-blue-500 dark:text-blue-400';
    }
  };

  const getStatusIcon = (status?: string) => {
    if (!status) return <Package2 className="h-6 w-6" />;
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'completed':
      case 'order concluded':
      case 'delivered':
        return <CheckCircle className="h-6 w-6" />;
      case 'pending':
      case 'processing':
        return <Clock className="h-6 w-6" />;
      case 'cancelled':
      case 'canceled':
        return <XCircle className="h-6 w-6" />;
      case 'shipped':
      case 'in transit':
        return <Truck className="h-6 w-6" />;
      default:
        return <Package2 className="h-6 w-6" />;
    }
  };

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(parseInt(timestamp) * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Date not available';
    }
  };

  const getDisplayStatus = () => {
    if (orderStatus?.order_status) {
      return orderStatus.order_status;
    }
    if (orderStatus?.payment_info?.orderStatus) {
      const statusMap: Record<string, string> = {
        '0': 'Pending',
        '1': 'Order Concluded',
        '2': 'Shipped',
        '3': 'Delivered',
        '4': 'Cancelled'
      };
      return statusMap[orderStatus.payment_info.orderStatus] || 'Unknown';
    }
    return 'Processing';
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Track your order status" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="relative bg-brand-primary py-24">
          {heroImage ? (
            <div className="absolute inset-0">
              <img 
                src={heroImage}
                alt="Tracking background"
                className="w-full h-full object-cover opacity-20"
              />
            </div>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-r from-brand-dark to-brand-primary" />
          )}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-display"
            >
              Track Your Order
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Monitor the status of your order
            </motion.p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6 print:hidden">
              <div className="relative">
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  placeholder="Enter your tracking number"
                  className="w-full h-14 pl-12 pr-4 rounded-2xl border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-lg font-medium"
              >
                {loading ? 'Tracking...' : 'Track Order'}
              </Button>
            </form>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl print:hidden"
                >
                  {error}
                </motion.div>
              )}

              {orderStatus?.status && orderStatus.orderDetails && orderStatus.orderDetails.length > 0 && (
                <motion.div
                  id="order-details-section"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-8 space-y-6"
                >
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Order Details
                    </h2>
                    <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2 no-print">
                      <Printer className="h-4 w-4" />
                      Print
                    </Button>
                  </div>

                  {/* Order Status Card */}
                  <div className="bg-gradient-to-r from-brand-primary to-brand-light rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full bg-white/20 ${getStatusColor(getDisplayStatus())}`}>
                          {getStatusIcon(getDisplayStatus())}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">Order Status</h3>
                          <p className="text-lg font-medium opacity-90">
                            {getDisplayStatus()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-75">Order ID</p>
                        <p className="text-lg font-bold">#{orderStatus.orderDetails[0].payment_id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Information Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Customer Information */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <User className="h-5 w-5 text-brand-primary" />
                        Customer Information
                      </h3>
                      <div className="space-y-3">
                        {orderStatus.payment_info && (
                          <>
                            {orderStatus.payment_info.customer_name && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Name:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {orderStatus.payment_info.customer_name}
                                </span>
                              </div>
                            )}
                            {orderStatus.payment_info.customer_email && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Email:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {orderStatus.payment_info.customer_email}
                                </span>
                              </div>
                            )}
                            {orderStatus.payment_info.customer_phone && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Phone:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {orderStatus.payment_info.customer_phone}
                                </span>
                              </div>
                            )}
                            {orderStatus.payment_info.payment_method && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Payment Method:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                  {orderStatus.payment_info.payment_method}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Order Details */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Package2 className="h-5 w-5 text-brand-primary" />
                        Order Information
                      </h3>
                      <div className="space-y-3">
                        {orderStatus.payment_info && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Order Date:</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatDate(orderStatus.payment_info.date)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Tracking ID:</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {orderStatus.payment_info.order_id}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Shipping Method:</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {orderStatus.payment_info.shipmentType}
                              </span>
                            </div>
                            {orderStatus.payment_info.note && (
                              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Note:</span>
                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                  {orderStatus.payment_info.note}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-brand-primary" />
                        Payment Summary
                      </h3>
                      {orderStatus.payment_info && (
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatPrice(orderStatus.payment_info.goods_amount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Shipping:</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatPrice(orderStatus.payment_info.shipping_fee)}
                            </span>
                          </div>
                          <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                            <div className="flex justify-between">
                              <span className="text-base font-bold text-gray-900 dark:text-white">Total:</span>
                              <span className="text-base font-bold text-brand-primary dark:text-brand-light">
                                {formatPrice(orderStatus.payment_info.goods_amount + orderStatus.payment_info.shipping_fee)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {orderStatus.payment_info?.shipping_address && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-brand-primary" />
                        Shipping Address
                      </h3>
                      <div className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                        {orderStatus.payment_info.shipping_address}
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Package2 className="h-5 w-5 text-brand-primary" />
                        Order Items ({orderStatus.orderDetails.length})
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {orderStatus.orderDetails.map((item, index) => (
                        <div key={index} className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">
                                {item.product_name}
                              </h4>
                              <div className="space-y-1">
                                {item.product_variant && item.product_variant !== 'Nil' && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">Variant:</span>
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                                      {item.product_variant}
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500 dark:text-gray-400">Quantity:</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {item.variant_qty}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500 dark:text-gray-400">Unit Price:</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatPrice(item.product_amount)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total</p>
                              <p className="text-xl font-bold text-brand-primary dark:text-brand-light">
                                {formatPrice(item.product_amount * item.variant_qty)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Order Timeline */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-brand-primary" />
                      Order Timeline
                    </h3>
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
                      <div className="space-y-6">
                        <div className="relative flex items-center">
                          <div className="flex-shrink-0 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center">
                            <CheckCircle className="h-4 w-4 text-white" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Order Placed</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {orderStatus.payment_info && formatDate(orderStatus.payment_info.date)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="relative flex items-center">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            getDisplayStatus().toLowerCase().includes('concluded') || 
                            getDisplayStatus().toLowerCase().includes('completed')
                              ? 'bg-green-500' 
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}>
                            {getStatusIcon(getDisplayStatus())}
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {getDisplayStatus()}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Current status of your order
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              <style jsx>{`
                @media print {
                  .no-print { display: none !important; }
                  #order-details-section {
                    background: white !important;
                    color: black !important;
                  }
                  #order-details-section * {
                    background: white !important;
                    color: black !important;
                  }
                }
              `}</style>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
}